import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/use-toast";
import { RefreshCw } from "lucide-react";
import { Unlock } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import AdminGuard from "@/admin/components/adminGuard";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import { BookingService } from "@/lib/services/bookingService";
import { SlotService } from "@/lib/services/slotService";
import { AdminBookingService } from "@/lib/services/adminBookingService";
import type { BookingWithDetails, Slot } from "@/lib/supabase";
import {
  Calendar,
  Clock,
  Users,
  LogOut,
  Check,
  X,
  Ban,
  Shield,
  Settings,
  Info,
  BookOpen,
  ArrowLeft,
  Download,
  FileText,
  AlertTriangle,
  History,
  MessageSquare,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import * as XLSX from "xlsx";

interface Teammate {
  name: string;
  phone_number?: string;
}

// Admin Session Service - handles blocking users during admin booking
class AdminSessionService {
  static async setAdminBookingSession(adminId: string, date: string) {
    try {
      const sessionData = {
        adminId,
        date,
        timestamp: new Date().toISOString(),
        active: true,
      };

      localStorage.setItem(
        "admin_booking_session",
        JSON.stringify(sessionData)
      );
      return true;
    } catch (error) {
      console.error("Error setting admin session:", error);
      return false;
    }
  }

  static async clearAdminBookingSession() {
    try {
      localStorage.removeItem("admin_booking_session");
      return true;
    } catch (error) {
      console.error("Error clearing admin session:", error);
      return false;
    }
  }

  static getActiveAdminSession() {
    try {
      const session = localStorage.getItem("admin_booking_session");
      if (!session) return null;

      const sessionData = JSON.parse(session);

      // Check if session is still valid (within 30 minutes)
      const sessionTime = new Date(sessionData.timestamp);
      const now = new Date();
      const timeDiff = now.getTime() - sessionTime.getTime();
      const thirtyMinutes = 30 * 60 * 1000;

      if (timeDiff > thirtyMinutes) {
        localStorage.removeItem("admin_booking_session");
        return null;
      }

      return sessionData.active ? sessionData : null;
    } catch (error) {
      console.error("Error getting admin session:", error);
      return null;
    }
  }

  static async extendAdminSession() {
    try {
      const session = this.getActiveAdminSession();
      if (session) {
        session.timestamp = new Date().toISOString();
        localStorage.setItem("admin_booking_session", JSON.stringify(session));
      }
    } catch (error) {
      console.error("Error extending admin session:", error);
    }
  }
}

const AdminDashboard = () => {
  const { user, logout, isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [bookings, setBookings] = useState<BookingWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterDate, setFilterDate] = useState<string>("");
  const [activeTab, setActiveTab] = useState("all");

  // Rejection dialog states
  const [rejectionDialog, setRejectionDialog] = useState({
    open: false,
    bookingId: "",
    customerName: "",
  });
  const [rejectionComment, setRejectionComment] = useState("");
  const [submittingRejection, setSubmittingRejection] = useState(false);

  // Admin booking states
  const [isAdminBooking, setIsAdminBooking] = useState(false);
  const [adminSelectedDate, setAdminSelectedDate] = useState("");
  const [adminSelectedSlots, setAdminSelectedSlots] = useState<string[]>([]);
  const [adminTeammates, setAdminTeammates] = useState<Teammate[]>([
    { name: "", phone_number: "" },
  ]);
  const [adminAvailableSlots, setAdminAvailableSlots] = useState<Slot[]>([]);
  const [adminLoadingSlots, setAdminLoadingSlots] = useState(false);
  const [adminSubmitting, setAdminSubmitting] = useState(false);

  // Blocking states
  const [blockingMode, setBlockingMode] = useState(false);
  const [blockingReason, setBlockingReason] = useState("");
  const [selectedSlotsToBlock, setSelectedSlotsToBlock] = useState<string[]>(
    []
  );
  const [blockingSlots, setBlockingSlots] = useState<Slot[]>([]);

  const loadData = async () => {
    setLoading(true);
    try {
      const result = await BookingService.getAllBookings();
      setBookings(result);
    } catch (err) {
      console.error("Failed to load data:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Session management
  const [sessionExtendInterval, setSessionExtendInterval] =
    useState<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (isLoading) return;

    if (!isAuthenticated || user?.type !== "admin") {
      navigate("/");
      return;
    }

    loadBookings();
  }, [isAuthenticated, user, navigate, isLoading]);

  useEffect(() => {
    if (adminSelectedDate && isAdminBooking) {
      loadAdminAvailableSlots();
    } else {
      setAdminAvailableSlots([]);
      setAdminSelectedSlots([]);
    }
  }, [adminSelectedDate, isAdminBooking]);

  // Session management effect
  useEffect(() => {
    if (isAdminBooking && adminSelectedDate) {
      AdminSessionService.setAdminBookingSession(
        user?.id || "",
        adminSelectedDate
      );

      const interval = setInterval(() => {
        AdminSessionService.extendAdminSession();
      }, 5 * 60 * 1000);

      setSessionExtendInterval(interval);

      toast({
        title: "Admin Booking Mode Active",
        description:
          "User bookings are now blocked for the selected date while you're booking.",
        duration: 5000,
      });
    } else {
      AdminSessionService.clearAdminBookingSession();

      if (sessionExtendInterval) {
        clearInterval(sessionExtendInterval);
        setSessionExtendInterval(null);
      }
    }

    return () => {
      if (sessionExtendInterval) {
        clearInterval(sessionExtendInterval);
      }
    };
  }, [isAdminBooking, adminSelectedDate, user?.id]);

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      AdminSessionService.clearAdminBookingSession();
      if (sessionExtendInterval) {
        clearInterval(sessionExtendInterval);
      }
    };
  }, []);

  const loadBookings = async () => {
    setLoading(true);
    try {
      const allBookings = await BookingService.getAllBookings();
      setBookings(allBookings);
    } catch (error) {
      console.error("Error loading bookings:", error);
      toast({
        title: "Error",
        description: "Failed to load bookings",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const loadAdminSlots = async () => {
    setAdminLoadingSlots(true);
    try {
      const slots = await SlotService.getAllSlotsForDate(adminSelectedDate);
      setAdminAvailableSlots(slots || []);
    } catch (error) {
      console.error("Error loading admin slots:", error);
      toast({
        title: "Error",
        description: "Failed to load slots. Please try again.",
        variant: "destructive",
      });
    } finally {
      setAdminLoadingSlots(false);
    }
  };

  const handleUnblockSlot = async (slotId: string) => {
    try {
      await SlotService.unblockSlots([slotId]);
      toast({ title: "Slot Unblocked" });
      loadAdminSlots();
    } catch (error) {
      console.error("Failed to unblock slot:", error);
      toast({
        title: "Error",
        description: "Failed to unblock the selected slot",
        variant: "destructive",
      });
    }
  };

  const loadAdminAvailableSlots = async () => {
    if (!adminSelectedDate) return;

    setAdminLoadingSlots(true);
    try {
      const existingSlots = await SlotService.getAllSlotsForDate(
        adminSelectedDate
      );

      if (existingSlots.length === 0) {
        await SlotService.createSlotsForDate(adminSelectedDate);
      }

      const slots = await SlotService.getAllSlotsForDate(adminSelectedDate);
      setAdminAvailableSlots(slots);
    } catch (error) {
      console.error("Error loading admin slots:", error);
      toast({
        title: "Error",
        description: "Failed to load slots",
        variant: "destructive",
      });
    } finally {
      setAdminLoadingSlots(false);
    }
  };

  const handleCancelBooking = async (bookingId: string) => {
    try {
      const success = await BookingService.adminCancelBooking(bookingId);

      if (success) {
        toast({
          title: "Booking Cancelled",
          description:
            "The booking has been cancelled and the slot is now available.",
        });
        loadBookings();
      } else {
        toast({
          title: "Cancellation Failed",
          description: "Failed to cancel booking. Please try again.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error cancelling booking:", error);
      toast({
        title: "Error",
        description: "An error occurred while cancelling the booking.",
        variant: "destructive",
      });
    }
  };

  const handleBookingAction = async (
    bookingId: string,
    action: "approved" | "rejected",
    comment?: string
  ) => {
    try {
      const success = await BookingService.updateBookingStatus(
        bookingId,
        action,
        comment
      );

      if (success) {
        toast({
          title: `Booking ${action.charAt(0).toUpperCase() + action.slice(1)}`,
          description: `Booking has been ${action} successfully.`,
        });
        loadBookings();
      } else {
        toast({
          title: "Action Failed",
          description: `Failed to ${action} booking. Please try again.`,
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error(`Error ${action} booking:`, error);
      toast({
        title: "Error",
        description: `An error occurred while ${
          action === "approved" ? "approving" : "rejecting"
        } the booking.`,
        variant: "destructive",
      });
    }
  };

  // Handle rejection with comment
  const handleRejectBooking = (bookingId: string, customerName: string) => {
    setRejectionDialog({
      open: true,
      bookingId,
      customerName,
    });
    setRejectionComment("");
  };

  const submitRejection = async () => {
    if (!rejectionComment.trim()) {
      toast({
        title: "Comment Required",
        description: "Please provide a reason for rejecting this booking.",
        variant: "destructive",
      });
      return;
    }

    setSubmittingRejection(true);
    try {
      await handleBookingAction(
        rejectionDialog.bookingId,
        "rejected",
        rejectionComment.trim()
      );
      
      setRejectionDialog({
        open: false,
        bookingId: "",
        customerName: "",
      });
      setRejectionComment("");
      
      toast({
        title: "Booking Rejected",
        description: "The booking has been rejected with your comment.",
      });
    } catch (error) {
      console.error("Error rejecting booking:", error);
      toast({
        title: "Error",
        description: "Failed to reject the booking. Please try again.",
        variant: "destructive",
      });
    } finally {
      setSubmittingRejection(false);
    }
  };

  // Admin booking functions
  const handleAdminSlotToggle = (slotId: string) => {
    if (blockingMode) {
      setSelectedSlotsToBlock((prev) =>
        prev.includes(slotId)
          ? prev.filter((id) => id !== slotId)
          : [...prev, slotId]
      );
    } else {
      setAdminSelectedSlots((prev) =>
        prev.includes(slotId)
          ? prev.filter((id) => id !== slotId)
          : [...prev, slotId]
      );
    }
  };

  const addAdminTeammate = () => {
    if (adminTeammates.length < 12) {
      setAdminTeammates([...adminTeammates, { name: "", phone_number: "" }]);
    }
  };

  const updateAdminTeammate = (
    index: number,
    field: "name" | "phone_number",
    value: string
  ) => {
    const updated = adminTeammates.map((teammate, i) =>
      i === index ? { ...teammate, [field]: value } : teammate
    );
    setAdminTeammates(updated);
  };

  const removeAdminTeammate = (index: number) => {
    if (adminTeammates.length > 1) {
      setAdminTeammates(adminTeammates.filter((_, i) => i !== index));
    }
  };

  const handleBlockSlots = async () => {
    if (!adminSelectedDate || selectedSlotsToBlock.length === 0) {
      toast({
        title: "Select Slots",
        description: "Please select slots to block",
        variant: "destructive",
      });
      return;
    }

    if (!blockingReason.trim()) {
      toast({
        title: "Blocking Reason Required",
        description: "Please provide a reason for blocking these slots",
        variant: "destructive",
      });
      return;
    }

    setAdminSubmitting(true);
    try {
      const success = await SlotService.blockSlots(
        selectedSlotsToBlock,
        blockingReason
      );

      if (success) {
        toast({
          title: "Slots Blocked",
          description: `${selectedSlotsToBlock.length} slots have been blocked successfully. User bookings have been cancelled.`,
        });

        setSelectedSlotsToBlock([]);
        setBlockingReason("");
        setBlockingMode(false);

        loadBookings();
        loadAdminAvailableSlots();
      } else {
        toast({
          title: "Blocking Failed",
          description: "Failed to block slots. Please try again.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error blocking slots:", error);
      toast({
        title: "Error",
        description: "An error occurred while blocking slots.",
        variant: "destructive",
      });
    } finally {
      setAdminSubmitting(false);
    }
  };

  const handleAdminBooking = async () => {
    if (!user || user.type !== "admin") return;

    if (!adminSelectedDate) {
      toast({
        title: "Select Date",
        description: "Please select a booking date",
        variant: "destructive",
      });
      return;
    }

    if (adminSelectedSlots.length === 0) {
      toast({
        title: "Select Time Slots",
        description: "Please select at least one time slot",
        variant: "destructive",
      });
      return;
    }

    const validTeammates = adminTeammates.filter((t) => t.name.trim() !== "");
    if (validTeammates.length === 0) {
      toast({
        title: "Add Team Members",
        description: "Please add at least one team member name",
        variant: "destructive",
      });
      return;
    }

    setAdminSubmitting(true);
    try {
      console.log("Booking Payload:", {
        user_id: user.id,
        slot_ids: adminSelectedSlots,
        team_members: validTeammates,
      });

      const bookingId = await AdminBookingService.createAdminBooking({
        user_id: user.id,
        slot_ids: adminSelectedSlots,
        team_members: validTeammates,
      });

      if (!bookingId) {
        toast({
          title: "Booking Failed",
          description: "Failed to create admin booking",
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Admin Booking Confirmed!",
        description: `Admin booking for ${adminSelectedDate} has been created successfully. Selected slots are now blocked for users.`,
      });

      AdminSessionService.clearAdminBookingSession();

      const confirmationData = {
        bookingId,
        date: adminSelectedDate,
        selectedSlots: adminAvailableSlots.filter((slot) =>
          adminSelectedSlots.includes(slot.id)
        ),
        teamMembers: validTeammates,
      };

      navigate("/admin/booking-confirmation", {
        state: { bookingData: confirmationData },
      });
    } catch (error) {
      console.error("Admin booking error:", error);
      toast({
        title: "Booking Failed",
        description: "An error occurred while processing admin booking",
        variant: "destructive",
      });
    } finally {
      setAdminSubmitting(false);
    }
  };

  const handleExitAdminBooking = () => {
    AdminSessionService.clearAdminBookingSession();

    setIsAdminBooking(false);
    setAdminSelectedDate("");
    setAdminSelectedSlots([]);
    setAdminTeammates([{ name: "", phone_number: "" }]);
    setBlockingMode(false);
    setSelectedSlotsToBlock([]);
    setBlockingReason("");

    toast({
      title: "Admin Booking Mode Exited",
      description: "User bookings are now enabled again.",
    });
  };

  const exportToExcel = (data: BookingWithDetails[], fileName: string) => {
    const formattedData = data.map((booking) => ({
      Date: booking.slot?.date || "N/A",
      "Time Slot": booking.slot
        ? formatTimeSlot(
            booking.slot.time_start,
            booking.slot.time_end
          )
        : "N/A",
      Status: booking.status.charAt(0).toUpperCase() + booking.status.slice(1),
      "Customer Name": booking.user?.username || "Unknown",
      "Customer Phone": booking.user?.phoneNo || "N/A",
      "Team Members": booking.team_members
        ? booking.team_members
            .map((m) => `${m.name}${m.phone_number ? ` (${m.phone_number})` : ""}`)
            .join(", ")
        : "N/A",
      "Rejection Reason": booking.rejection_reason || booking.admin_comment || "N/A",
    }));

    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Bookings");
    XLSX.writeFile(workbook, `${fileName}.xlsx`);
  };

  const handleLogout = () => {
    AdminSessionService.clearAdminBookingSession();
    logout();
    navigate("/admin-login");
  };

  const filteredBookings = bookings.filter((booking) => {
    if (!filterDate) return true;
    // Normalize date comparison - handle different date formats
    const bookingDate = booking.slot?.date;
    const filterDateNormalized = filterDate;
    
    // Log for debugging
    if (bookingDate && bookingDate !== filterDateNormalized) {
      console.debug(`Date mismatch: booking date=${bookingDate}, filter date=${filterDateNormalized}`);
    }
    
    return bookingDate === filterDateNormalized;
  });
  
  // Debug logging
  useEffect(() => {
    if (filterDate) {
      console.log(`Filtering bookings by date: ${filterDate}`);
      console.log(`Total bookings: ${bookings.length}`);
      console.log(`Filtered bookings: ${filteredBookings.length}`);
      console.log(`Bookings by date:`, bookings.reduce((acc, b) => {
        const date = b.slot?.date || 'unknown';
        acc[date] = (acc[date] || 0) + 1;
        return acc;
      }, {} as Record<string, number>));
    }
  }, [filterDate, bookings.length]);

  const formatTimeSlot = (timeStart: string, timeEnd: string) => {
    const formatTime = (time: string) => {
      const [hours, minutes] = time.split(":");
      const hour = Number.parseInt(hours);
      const ampm = hour >= 12 ? "PM" : "AM";
      const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
      return `${displayHour}:${minutes} ${ampm}`;
    };

    return `${formatTime(timeStart)} - ${formatTime(timeEnd)}`;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "approved":
        return "bg-green-100 text-green-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "rejected":
        return "bg-red-100 text-red-800";
      case "cancelled":
        return "bg-gray-100 text-gray-800";
      case "booked":
        return "bg-blue-100 text-blue-800";
      case "paid":
        return "bg-purple-100 text-purple-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const minDate = new Date();
  const minDateString = minDate.toISOString().split("T")[0];

  const maxDate = new Date();
  maxDate.setDate(maxDate.getDate() + 30);
  const maxDateString = maxDate.toISOString().split("T")[0];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-green-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) return null;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-green-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p>Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  const pendingBookings = filteredBookings.filter(
    (b) => b.status === "pending"
  );
  const approvedBookings = filteredBookings.filter(
    (b) => b.status === "approved"
  );
  const rejectedBookings = filteredBookings.filter(
    (b) => b.status === "rejected"
  );
  const cancelledBookings = filteredBookings.filter(
    (b) => b.status === "cancelled"
  );
  const bookedBookings = filteredBookings.filter(
    (b) => b.status === "booked"
  );
  const paidBookings = filteredBookings.filter(
    (b) => b.status === "paid"
  );
  const otherBookings = filteredBookings.filter(
    (b) => !["pending", "approved", "rejected", "cancelled", "booked", "paid"].includes(b.status)
  );

  // Admin Booking Page Component
  if (isAdminBooking) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-green-50">
        <header className="bg-white shadow-sm border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center py-4">
              <Button
                variant="ghost"
                onClick={handleExitAdminBooking}
                className="mr-4"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Dashboard
              </Button>
              <h1 className="text-2xl font-bold text-blue-900">
                Admin Slot Management
              </h1>
              <div className="ml-auto">
                <Alert className="inline-flex items-center px-3 py-2 bg-orange-50 border-orange-200">
                  <AlertTriangle className="h-4 w-4 mr-2 text-orange-600" />
                  <span className="text-sm text-orange-800 font-medium">
                    User bookings are blocked while admin is booking
                  </span>
                </Alert>
              </div>
            </div>
          </div>
        </header>

        <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Calendar className="w-5 h-5 mr-2" />
                    Select Date
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="admin-date">Admin Booking Date</Label>
                    <Input
                      id="admin-date"
                      type="date"
                      min={minDateString}
                      max={maxDateString}
                      value={adminSelectedDate}
                      onChange={(e) => setAdminSelectedDate(e.target.value)}
                    />
                  </div>
                  {adminSelectedDate && (
                    <Alert>
                      <Shield className="h-4 w-4" />
                      <AlertDescription>
                        <strong>Admin Session Active:</strong> All user bookings
                        for {adminSelectedDate} are temporarily blocked.
                      </AlertDescription>
                    </Alert>
                  )}
                </CardContent>
              </Card>

              {adminSelectedDate && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center justify-between">
                      <div className="flex items-center">
                        <Clock className="w-5 h-5 mr-2" />
                        {blockingMode
                          ? "Block Time Slots"
                          : "Available Time Slots"}
                      </div>
                      <div className="flex space-x-2">
                        <Button
                          variant={blockingMode ? "default" : "outline"}
                          size="sm"
                          onClick={() => {
                            setBlockingMode(!blockingMode);
                            setSelectedSlotsToBlock([]);
                            setAdminSelectedSlots([]);
                          }}
                        >
                          <Ban className="w-4 h-4 mr-2" />
                          {blockingMode ? "Exit Block Mode" : "Block Slots"}
                        </Button>
                      </div>
                    </CardTitle>
                    <CardDescription>
                      {blockingMode
                        ? "Select slots to manually block (for maintenance, government visits, etc.)"
                        : "Select multiple time slots for admin booking - these will be automatically blocked for users"}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {blockingMode && (
                      <div className="mb-4">
                        <Label htmlFor="blocking-reason">Blocking Reason</Label>
                        <Input
                          id="blocking-reason"
                          placeholder="e.g., Government Authority Visit, Maintenance, etc."
                          value={blockingReason}
                          onChange={(e) => setBlockingReason(e.target.value)}
                        />
                      </div>
                    )}

                    {adminLoadingSlots ? (
                      <div className="text-center py-4">Loading slots...</div>
                    ) : (
                      <div className="grid grid-cols-1 gap-3">
                        {adminAvailableSlots.map((slot) => {
                          const isUserBooked =
                            slot.booked_by_user_id !== null &&
                            slot.block_reason !== "Admin Booking";
                          const isAdminBooked =
                            slot.block_reason === "Admin Booking";
                          const isManuallyBlocked =
                            slot.status === "blocked" &&
                            slot.block_reason !== "Admin Booking";
                          const isSelected = blockingMode
                            ? selectedSlotsToBlock.includes(slot.id)
                            : adminSelectedSlots.includes(slot.id);

                          let statusBg =
                            "bg-white border-gray-200 hover:bg-gray-50";
                          let statusText = "Available";
                          let statusColor = "text-green-600";

                          if (isManuallyBlocked) {
                            statusBg = "bg-red-50 border-red-300";
                            statusText = "Manually Blocked";
                            statusColor = "text-red-600";
                          } else if (isAdminBooked) {
                            statusBg = "bg-blue-50 border-blue-300";
                            statusText = "Admin Booked";
                            statusColor = "text-blue-600";
                          } else if (isUserBooked) {
                            statusBg = "bg-orange-50 border-orange-300";
                            statusText = "User Booked";
                            statusColor = "text-orange-600";
                          } else if (isSelected) {
                            statusBg =
                              "bg-green-50 border-green-300 ring-2 ring-green-200";
                            statusText = "Selected";
                            statusColor = "text-green-600 font-medium";
                          }

                          return (
                            <div
                              key={slot.id}
                              className={`flex items-center space-x-3 p-3 rounded-lg border transition-all hover:shadow-md ${statusBg}`}
                            >
                              <Checkbox
                                id={slot.id}
                                checked={isSelected}
                                disabled={
                                  !blockingMode &&
                                  (isManuallyBlocked || isAdminBooked)
                                }
                                onCheckedChange={() =>
                                  handleAdminSlotToggle(slot.id)
                                }
                              />
                              <label
                                htmlFor={slot.id}
                                className="flex-1 cursor-pointer"
                              >
                                <div className="flex justify-between items-center">
                                  <span
                                    className={`font-medium ${
                                      isManuallyBlocked ? "text-red-500" : ""
                                    }`}
                                  >
                                    {formatTimeSlot(
                                      slot.time_start,
                                      slot.time_end
                                    )}
                                  </span>
                                  <div className="flex items-center space-x-2">
                                    {isManuallyBlocked && (
                                      <>
                                        <Badge
                                          variant="destructive"
                                          className="text-xs"
                                        >
                                          <Ban className="w-2 h-2 mr-1" />
                                          Blocked
                                        </Badge>
                                        <Button
                                          className="bg-green-600 hover:bg-green-700 text-white font-medium shadow-md"
                                          size="sm"
                                          onClick={() =>
                                            handleUnblockSlot(slot.id)
                                          }
                                        >
                                          <Unlock className="w-4 h-4 mr-1" />
                                          Unblock
                                        </Button>
                                      </>
                                    )}
                                    {isAdminBooked && (
                                      <Badge className="text-xs bg-blue-100 text-blue-800">
                                        <Shield className="w-3 h-3 mr-1" />
                                        Admin Booked
                                      </Badge>
                                    )}
                                    {isUserBooked &&
                                      !isAdminBooked &&
                                      !isManuallyBlocked && (
                                        <Badge className="text-xs bg-orange-100 text-orange-800">
                                          User Booked
                                        </Badge>
                                      )}
                                    <span className={`text-sm ${statusColor}`}>
                                      {statusText}
                                    </span>
                                  </div>
                                </div>
                                {slot.block_reason && (
                                  <div className="text-xs text-gray-600 mt-1">
                                    Reason: {slot.block_reason}
                                  </div>
                                )}
                              </label>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {blockingMode && selectedSlotsToBlock.length > 0 && (
                      <div className="mt-4">
                        <Alert className="mb-4">
                          <AlertTriangle className="h-4 w-4" />
                          <AlertDescription>
                            <strong>Warning:</strong> Blocking these slots will
                            automatically cancel any existing user bookings for
                            the selected time slots.
                          </AlertDescription>
                        </Alert>
                        <Button
                          onClick={handleBlockSlots}
                          disabled={adminSubmitting || !blockingReason.trim()}
                          className="w-full bg-red-600 hover:bg-red-700"
                        >
                          {adminSubmitting
                            ? "Blocking..."
                            : `Block ${selectedSlotsToBlock.length} Slots & Cancel User Bookings`}
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>

            <div className="space-y-6">
              {!blockingMode && (
                <>
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center">
                        <Users className="w-5 h-5 mr-2" />
                        Team Members
                      </CardTitle>
                      <CardDescription>
                        Add team members for admin booking
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {adminTeammates.map((teammate, index) => (
                        <div key={index} className="flex space-x-2">
                          <div className="flex-1">
                            <Input
                              placeholder="Team member name *"
                              value={teammate.name}
                              onChange={(e) => {
                                const input = e.target.value.replace(
                                  /[^a-zA-Z\s]/g,
                                  ""
                                );
                                updateAdminTeammate(index, "name", input);
                              }}
                            />
                          </div>
                          <div className="flex-1">
                            <Input
                              placeholder="Mobile (optional)"
                              value={teammate.phone_number}
                              onChange={(e) => {
                                const digitsOnly = e.target.value
                                  .replace(/\D/g, "")
                                  .slice(0, 10);
                                updateAdminTeammate(
                                  index,
                                  "phone_number",
                                  digitsOnly
                                );
                              }}
                            />
                          </div>
                          {adminTeammates.length > 1 && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => removeAdminTeammate(index)}
                            >
                              Remove
                            </Button>
                          )}
                        </div>
                      ))}

                      {adminTeammates.length < 12 && (
                        <Button
                          variant="outline"
                          onClick={addAdminTeammate}
                          className="w-full"
                        >
                          Add Team Member
                        </Button>
                      )}
                    </CardContent>
                  </Card>

                  {adminSelectedSlots.length > 0 && (
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center">
                          <BookOpen className="w-5 h-5 mr-2" />
                          Admin Booking Summary
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span>Date:</span>
                            <span className="font-medium">
                              {adminSelectedDate}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span>Selected Slots:</span>
                            <span className="font-medium">
                              {adminSelectedSlots.length}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span>Team Size:</span>
                            <span className="font-medium">
                              {
                                adminTeammates.filter((t) => t.name.trim())
                                  .length
                              }
                            </span>
                          </div>
                        </div>

                        <Alert className="mt-4">
                          <Shield className="h-4 w-4" />
                          <AlertDescription>
                            <strong>Admin Booking:</strong> This booking will be
                            automatically approved and the selected slots will
                            be blocked for users to prevent double booking.
                          </AlertDescription>
                        </Alert>

                        <Button
                          onClick={handleAdminBooking}
                          className="w-full mt-4 bg-blue-600 hover:bg-blue-700"
                          disabled={adminSubmitting}
                        >
                          {adminSubmitting
                            ? "Creating Admin Booking..."
                            : "Confirm Admin Booking & Block Slots"}
                        </Button>
                      </CardContent>
                    </Card>
                  )}
                </>
              )}

              {blockingMode && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <Info className="w-5 h-5 mr-2" />
                      Manual Slot Blocking Information
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Alert>
                      <Ban className="h-4 w-4" />
                      <AlertDescription>
                        <strong>Manual Blocking:</strong>
                        <ul className="mt-2 space-y-1 text-sm">
                          <li>
                            • Use this for maintenance, government visits, etc.
                          </li>
                          <li>
                            • <strong>Different from admin booking</strong> -
                            these slots won't have any booking record
                          </li>
                          <li>• Existing user bookings will be cancelled</li>
                          <li>• Provide a clear reason for blocking</li>
                          <li>
                            • Blocked slots can be unblocked later if needed
                          </li>
                          <li>
                            • Admin bookings automatically block slots - no need
                            to manually block
                          </li>
                        </ul>
                      </AlertDescription>
                    </Alert>

                    {selectedSlotsToBlock.length > 0 && (
                      <div className="mt-4 p-3 bg-red-50 rounded-lg">
                        <h4 className="font-medium text-red-800 mb-2">
                          Slots to Block: {selectedSlotsToBlock.length}
                        </h4>
                        <div className="text-sm text-red-700 mb-2">
                          {selectedSlotsToBlock
                            .map((slotId) => {
                              const slot = adminAvailableSlots.find(
                                (s) => s.id === slotId
                              );
                              return slot
                                ? formatTimeSlot(slot.time_start, slot.time_end)
                                : "";
                            })
                            .join(", ")}
                        </div>
                        <div className="text-xs text-red-600 font-medium">
                          {
                            selectedSlotsToBlock.filter((slotId) => {
                              const slot = adminAvailableSlots.find(
                                (s) => s.id === slotId
                              );
                              return (
                                slot?.booked_by_user_id !== null &&
                                slot?.block_reason !== "Admin Booking"
                              );
                            }).length
                          }{" "}
                          user bookings will be cancelled
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Main Dashboard
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-green-50">
      {/* Rejection Comment Dialog */}
      <Dialog open={rejectionDialog.open} onOpenChange={(open) => 
        setRejectionDialog(prev => ({ ...prev, open }))
      }>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center text-red-600">
              <MessageSquare className="w-5 h-5 mr-2" />
              Reject Booking
            </DialogTitle>
            <DialogDescription>
              Provide a reason for rejecting <strong>{rejectionDialog.customerName}'s</strong> booking. 
              This comment will be visible to the customer.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="rejection-comment" className="text-sm font-medium">
                Rejection Reason *
              </Label>
              <Textarea
                id="rejection-comment"
                placeholder="e.g., Requested time slot is not available due to maintenance, Please choose alternative time slots, etc."
                value={rejectionComment}
                onChange={(e) => setRejectionComment(e.target.value)}
                className="mt-2 min-h-[100px] resize-none"
                maxLength={500}
              />
              <div className="text-xs text-gray-500 mt-1">
                {rejectionComment.length}/500 characters
              </div>
            </div>
            
            <Alert className="border-orange-200 bg-orange-50">
              <Info className="h-4 w-4 text-orange-600" />
              <AlertDescription className="text-orange-800">
                <strong>Note:</strong> The customer will receive this comment along with the rejection notification. 
                Please be professional and helpful.
              </AlertDescription>
            </Alert>
          </div>

          <DialogFooter className="flex space-x-2">
            <Button
              variant="outline"
              onClick={() => {
                setRejectionDialog({ open: false, bookingId: "", customerName: "" });
                setRejectionComment("");
              }}
              disabled={submittingRejection}
            >
              Cancel
            </Button>
            <Button
              onClick={submitRejection}
              disabled={submittingRejection || !rejectionComment.trim()}
              className="bg-red-600 hover:bg-red-700"
            >
              {submittingRejection ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Rejecting...
                </>
              ) : (
                <>
                  <X className="w-4 h-4 mr-2" />
                  Reject Booking
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <h1 className="text-2xl font-bold text-blue-900">
                Admin Dashboard
              </h1>
              <p className="text-gray-600">
                Gamesville Turf Management
              </p>
            </div>
            <div className="flex space-x-2">
              <Button onClick={loadData} disabled={loading}>
                {loading ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                    Refreshing...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Refresh
                  </>
                )}
              </Button>

              <Button
                variant="outline"
                onClick={() => navigate("/admin/booking-history")}
                className="bg-purple-600 hover:bg-purple-700 text-white"
              >
                <History className="w-4 h-4 mr-2" />
                Admin History
              </Button>

              <Button
                variant="outline"
                onClick={() => navigate("/manual-blocking-history")}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                <Ban className="w-4 h-4 mr-2" />
                Block History
              </Button>

              <Button
                variant="default"
                onClick={() => setIsAdminBooking(true)}
                className="bg-green-600 hover:bg-green-700"
              >
                <Settings className="w-4 h-4 mr-2" />
                Admin Book Slot
              </Button>
              <Button variant="outline" onClick={handleLogout}>
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
          <h2 className="text-2xl font-bold text-gray-900">
            User Booking Management
          </h2>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <Label htmlFor="filter-date" className="whitespace-nowrap">
                Filter by Date:
              </Label>
              <Input
                id="filter-date"
                type="date"
                value={filterDate}
                onChange={(e) => setFilterDate(e.target.value)}
                className="max-w-[180px]"
              />
              {filterDate && (
                <Button
                  variant="ghost"
                  onClick={() => setFilterDate("")}
                  className="text-red-600 hover:text-red-700"
                >
                  Clear
                </Button>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-yellow-600">
                    {pendingBookings.length}
                  </p>
                  <p className="text-sm text-gray-600">Pending Bookings</p>
                </div>
                <Clock className="w-8 h-8 text-yellow-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-green-600">
                    {approvedBookings.length}
                  </p>
                  <p className="text-sm text-gray-600">Approved Bookings</p>
                </div>
                <Check className="w-8 h-8 text-green-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-red-600">
                    {rejectedBookings.length}
                  </p>
                  <p className="text-sm text-gray-600">Rejected Bookings</p>
                </div>
                <X className="w-8 h-8 text-red-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-blue-600">
                    {filteredBookings.length}
                  </p>
                  <p className="text-sm text-gray-600">Total Bookings</p>
                </div>
                <FileText className="w-8 h-8 text-blue-600" />
              </div>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle>User Booking Management</CardTitle>
              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  onClick={() =>
                    exportToExcel(filteredBookings, "user_bookings")
                  }
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export All
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="all">
                  All ({filteredBookings.length})
                </TabsTrigger>
                <TabsTrigger value="pending">
                  Pending ({pendingBookings.length})
                </TabsTrigger>
                <TabsTrigger value="approved">
                  Approved ({approvedBookings.length})
                </TabsTrigger>
                <TabsTrigger value="rejected">
                  Rejected ({rejectedBookings.length})
                </TabsTrigger>
              </TabsList>

              <TabsContent value="all" className="space-y-4">
                {filteredBookings.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    No bookings found.
                  </div>
                ) : (
                  filteredBookings.map((booking) => (
                    <Card
                      key={booking.id}
                      className={`border-l-4 ${
                        booking.status === "pending"
                          ? "border-l-yellow-500"
                          : booking.status === "approved"
                          ? "border-l-green-500"
                          : booking.status === "rejected"
                          ? "border-l-red-500"
                          : booking.status === "cancelled"
                          ? "border-l-gray-500"
                          : booking.status === "booked"
                          ? "border-l-blue-500"
                          : booking.status === "paid"
                          ? "border-l-purple-500"
                          : "border-l-gray-400"
                      }`}
                    >
                      <CardContent className="p-6">
                        <div className="flex justify-between items-start">
                          <div className="space-y-2">
                            <div className="flex items-center space-x-4">
                              <Badge className={getStatusColor(booking.status)}>
                                {booking.status.charAt(0).toUpperCase() +
                                  booking.status.slice(1)}
                              </Badge>
                              <span className="text-sm text-gray-500">
                                {new Date(
                                  booking.created_at
                                ).toLocaleDateString()}
                              </span>
                            </div>
                            <h3 className="font-semibold text-lg">
                              {booking.user?.username || "Unknown"} - {booking.user?.phoneNo || "N/A"}
                            </h3>
                            <div className="flex items-center text-gray-600">
                              <Calendar className="w-4 h-4 mr-2" />
                              {booking.slot?.date || "N/A"}
                            </div>
                            <div className="flex items-center text-gray-600">
                              <Clock className="w-4 h-4 mr-2" />
                              {booking.slot
                                ? formatTimeSlot(
                                    booking.slot.time_start,
                                    booking.slot.time_end
                                  )
                                : "N/A"}
                            </div>
                            <div className="flex items-center text-gray-600">
                              <Users className="w-4 h-4 mr-2" />
                              {booking.team_members?.length || 0} team members
                            </div>
                            {booking.team_members && booking.team_members.length > 0 && (
                              <div className="text-sm text-gray-500">
                                Team:{" "}
                                {booking.team_members
                                  .map((m) => m.name)
                                  .join(", ")}
                              </div>
                            )}
                            {booking.rejection_reason && (
                              <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                                <div className="flex items-start space-x-2">
                                  <MessageSquare className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                                  <div className="flex-1">
                                    <p className="text-sm font-medium text-red-800 mb-1">
                                      Rejection Reason:
                                    </p>
                                    <p className="text-sm text-red-700">
                                      {booking.rejection_reason}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                          <div className="flex space-x-2">
                            {booking.status === "pending" && (
                              <>
                                <Button
                                  size="sm"
                                  onClick={() =>
                                    handleBookingAction(booking.id, "approved")
                                  }
                                  className="bg-green-600 hover:bg-green-700"
                                >
                                  <Check className="w-4 h-4 mr-1" />
                                  Approve
                                </Button>
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  onClick={() =>
                                    handleRejectBooking(booking.id, booking.user?.username || "Customer")
                                  }
                                >
                                  <MessageSquare className="w-4 h-4 mr-1" />
                                  Reject
                                </Button>
                              </>
                            )}
                            {(booking.status === "approved" || booking.status === "booked" || booking.status === "paid") && (
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => handleCancelBooking(booking.id)}
                              >
                                <X className="w-4 h-4 mr-1" />
                                Cancel
                              </Button>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </TabsContent>

              <TabsContent value="pending" className="space-y-4">
                {pendingBookings.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    No pending bookings found.
                  </div>
                ) : (
                  pendingBookings.map((booking) => (
                    <Card
                      key={booking.id}
                      className="border-l-4 border-l-yellow-500"
                    >
                      <CardContent className="p-6">
                        <div className="flex justify-between items-start">
                          <div className="space-y-2">
                            <div className="flex items-center space-x-4">
                              <Badge className={getStatusColor(booking.status)}>
                                {booking.status.charAt(0).toUpperCase() +
                                  booking.status.slice(1)}
                              </Badge>
                              <span className="text-sm text-gray-500">
                                {new Date(
                                  booking.created_at
                                ).toLocaleDateString()}
                              </span>
                            </div>
                            <h3 className="font-semibold text-lg">
                              {booking.user?.username || "Unknown"} - {booking.user?.phoneNo || "N/A"}
                            </h3>
                            <div className="flex items-center text-gray-600">
                              <Calendar className="w-4 h-4 mr-2" />
                              {booking.slot?.date || "N/A"}
                            </div>
                            <div className="flex items-center text-gray-600">
                              <Clock className="w-4 h-4 mr-2" />
                              {booking.slot
                                ? formatTimeSlot(
                                    booking.slot.time_start,
                                    booking.slot.time_end
                                  )
                                : "N/A"}
                            </div>
                            <div className="flex items-center text-gray-600">
                              <Users className="w-4 h-4 mr-2" />
                              {booking.team_members?.length || 0} team members
                            </div>
                            {booking.team_members && booking.team_members.length > 0 && (
                              <div className="text-sm text-gray-500">
                                Team:{" "}
                                {booking.team_members
                                  .map((m) => m.name)
                                  .join(", ")}
                              </div>
                            )}
                          </div>
                          <div className="flex space-x-2">
                            <Button
                              size="sm"
                              onClick={() =>
                                handleBookingAction(booking.id, "approved")
                              }
                              className="bg-green-600 hover:bg-green-700"
                            >
                              <Check className="w-4 h-4 mr-1" />
                              Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() =>
                                handleRejectBooking(booking.id, booking.user?.username || "Customer")
                              }
                            >
                              <MessageSquare className="w-4 h-4 mr-1" />
                              Reject with Comment
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </TabsContent>

              <TabsContent value="approved" className="space-y-4">
                {approvedBookings.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    No approved bookings found.
                  </div>
                ) : (
                  approvedBookings.map((booking) => (
                    <Card
                      key={booking.id}
                      className="border-l-4 border-l-green-500"
                    >
                      <CardContent className="p-6">
                        <div className="flex justify-between items-start">
                          <div className="space-y-2">
                            <div className="flex items-center space-x-4">
                              <Badge className={getStatusColor(booking.status)}>
                                {booking.status.charAt(0).toUpperCase() +
                                  booking.status.slice(1)}
                              </Badge>
                              <span className="text-sm text-gray-500">
                                {new Date(
                                  booking.created_at
                                ).toLocaleDateString()}
                              </span>
                            </div>
                            <h3 className="font-semibold text-lg">
                              {booking.user?.username || "Unknown"} - {booking.user?.phoneNo || "N/A"}
                            </h3>
                            <div className="flex items-center text-gray-600">
                              <Calendar className="w-4 h-4 mr-2" />
                              {booking.slot?.date || "N/A"}
                            </div>
                            <div className="flex items-center text-gray-600">
                              <Clock className="w-4 h-4 mr-2" />
                              {booking.slot
                                ? formatTimeSlot(
                                    booking.slot.time_start,
                                    booking.slot.time_end
                                  )
                                : "N/A"}
                            </div>
                            <div className="flex items-center text-gray-600">
                              <Users className="w-4 h-4 mr-2" />
                              {booking.team_members?.length || 0} team members
                            </div>
                            {booking.team_members && booking.team_members.length > 0 && (
                              <div className="text-sm text-gray-500">
                                Team:{" "}
                                {booking.team_members
                                  .map((m) => m.name)
                                  .join(", ")}
                              </div>
                            )}
                          </div>
                          <div className="flex space-x-2">
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleCancelBooking(booking.id)}
                            >
                              <X className="w-4 h-4 mr-1" />
                              Cancel
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </TabsContent>

              <TabsContent value="rejected" className="space-y-4">
                {rejectedBookings.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    No rejected bookings found.
                  </div>
                ) : (
                  rejectedBookings.map((booking) => (
                    <Card
                      key={booking.id}
                      className="border-l-4 border-l-red-500"
                    >
                      <CardContent className="p-6">
                        <div className="space-y-2">
                          <div className="flex items-center space-x-4">
                            <Badge className={getStatusColor(booking.status)}>
                              {booking.status.charAt(0).toUpperCase() +
                                booking.status.slice(1)}
                            </Badge>
                            <span className="text-sm text-gray-500">
                              {new Date(
                                booking.created_at
                              ).toLocaleDateString()}
                            </span>
                          </div>
                          <h3 className="font-semibold text-lg">
                            {booking.user?.username || "Unknown"} - {booking.user?.phoneNo || "N/A"}
                          </h3>
                          <div className="flex items-center text-gray-600">
                            <Calendar className="w-4 h-4 mr-2" />
                            {booking.slot?.date || "N/A"}
                          </div>
                          <div className="flex items-center text-gray-600">
                            <Clock className="w-4 h-4 mr-2" />
                            {booking.slot
                              ? formatTimeSlot(
                                  booking.slot.time_start,
                                  booking.slot.time_end
                                )
                              : "N/A"}
                          </div>
                          <div className="flex items-center text-gray-600">
                            <Users className="w-4 h-4 mr-2" />
                            {booking.team_members?.length || 0} team members
                          </div>
                          {booking.team_members && booking.team_members.length > 0 && (
                            <div className="text-sm text-gray-500">
                              Team:{" "}
                              {booking.team_members.map((m) => m.name).join(", ")}
                            </div>
                          )}
                          {booking.admin_comment && (
                            <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                              <div className="flex items-start space-x-2">
                                <MessageSquare className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                                <div className="flex-1">
                                  <p className="text-sm font-medium text-red-800 mb-1">
                                    Admin Rejection Reason:
                                  </p>
                                  <p className="text-sm text-red-700">
                                    {booking.admin_comment}
                                  </p>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default AdminDashboard;